import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { downloadHealthReportPDF } from "@/lib/pdfReport";
import { unlockAchievement } from "@/components/silentscan/GamificationSection";
import HealthTwin from "@/components/silentscan/HealthTwin";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { saveRecord } from "@/lib/records";
import { buildHistoryContext } from "@/lib/deviceHistory";
import {
  Bot,
  User,
  Send,
  Loader2,
  RefreshCcw,
  Check,
  Sparkles,
  Utensils,
  Gauge,
  X,
  Sun,
  Sunset,
  Moon,
  Cookie,
  AlertTriangle,
  ShieldAlert,
  Info,
  Moon as MoonIcon,
  Activity,
  Droplets,
  Salad,
  Brain,
  Cigarette,
  HeartPulse,
  Users,
  Stethoscope,
  Phone,
  TrendingUp,
  TrendingDown,
  Minus,
  ClipboardCheck,
  Pencil,
  Save,
  Scale,
  Mic,
  MicOff,
  Download,
  Share2,
  Loader2 as Spinner,
} from "lucide-react";
import { toast } from "sonner";
import { useChatbot } from "@/context/ChatbotContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatbotDialog() {
  const { open, setOpen, closeChatbot } = useChatbot();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(16);
  const [assessment, setAssessment] = useState(null);
  const [tab, setTab] = useState("chat"); // chat | score | diet
  const [emergency, setEmergency] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const scrollRef = useRef(null);

  const startChat = async () => {
    if (starting) return;
    setStarting(true);
    setAssessment(null);
    setEmergency(null);
    setReviewItems([]);
    setMessages([]);
    setProgress(0);
    setTab("chat");
    try {
      const { data } = await axios.post(`${API}/chat/start`);
      setSessionId(data.session_id);
      setTotal(data.total);
      setProgress(data.progress);
      setMessages([{ role: "assistant", text: data.assistant_message }]);
    } catch (e) {
      console.error(e);
      toast.error("Could not start the chat. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  // Start / reset when dialog opens
  useEffect(() => {
    if (open && !sessionId) startChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, assessment, tab]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending || !sessionId) return;

    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
        history_context: buildHistoryContext(),
      });
      setProgress(data.progress);
      setMessages((m) => [
        ...m,
        { role: "assistant", text: data.assistant_message },
      ]);
      if (data.emergency) {
        setEmergency(data.emergency_message || data.assistant_message);
        toast.error("Emergency indicators detected");
        return;
      }
      if (data.review) {
        setReviewItems(data.review_items || []);
        setTab("review");
        toast.success("Please review your information before we generate your score");
      }
      if (data.needs_correction) {
        // Answer was invalid — keep progress where it is
      }
      if (data.done && data.assessment && !assessment) {
        setAssessment(data.assessment);
        setTab("score");
        toast.success("Your health assessment is ready");
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const restart = () => {
    setSessionId(null);
    setAssessment(null);
    setEmergency(null);
    setReviewItems([]);
    setProgress(0);
    setMessages([]);
    setTab("chat");
    startChat();
  };

  const editAnswer = async (questionId, newAnswer) => {
    if (!sessionId) return false;
    try {
      const { data } = await axios.post(`${API}/chat/edit`, {
        session_id: sessionId,
        question_id: questionId,
        new_answer: newAnswer,
      });
      if (data.needs_correction) {
        toast.error(data.assistant_message);
        return false;
      }
      setReviewItems(data.review_items || []);
      toast.success("Updated");
      return true;
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Could not save the change";
      toast.error(msg);
      return false;
    }
  };

  const confirmAndGenerate = async () => {
    if (!sessionId || confirming) return;
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/chat/confirm`, {
        session_id: sessionId,
      });
      if (data.done && data.assessment) {
        setAssessment(data.assessment);
        setTab("score");
        unlockAchievement("health_check", 50);
        toast.success("Your health assessment is ready");
      }
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Could not generate assessment";
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  const progressPct = Math.round((progress / total) * 100);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay className="bg-black/80 backdrop-blur-md z-50" />
        <DialogContent
          data-testid="chatbot-dialog"
          className="fixed left-[50%] top-[50%] z-50 w-[95vw] max-w-4xl h-[90dvh] max-h-[820px] -translate-x-1/2 -translate-y-1/2 p-0 border-0 bg-transparent shadow-none [&>button.absolute]:hidden"
        >
          <div className="relative w-full h-full overflow-hidden rounded-3xl glass-card border border-white/10 bg-[#05050A]/95 shadow-[0_40px_120px_rgba(0,229,255,0.25)]">
            <DialogTitle className="sr-only">SilentScan AI Health Assessment</DialogTitle>
            <DialogDescription className="sr-only">
              AI-powered health chatbot that asks preventive health questions and generates a personalized Health Risk Score and 7-day diet plan.
            </DialogDescription>
            {/* Header */}
            <div className="flex items-center justify-between px-5 sm:px-7 py-4 border-b border-white/5 bg-black/40 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_20px_rgba(0,229,255,0.35)]">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-display text-base font-semibold">
                    SilentScan AI
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-400 whitespace-nowrap">
                    {assessment ? "Assessment complete" : "SilentScan GPT-6 Astra"}
                  </div>
                </div>
              </div>

              {/* Tab pills */}
              <div className="hidden sm:flex items-center gap-1 rounded-full bg-white/[0.03] border border-white/10 p-1">
                <TabPill
                  label="Chat"
                  icon={Bot}
                  active={tab === "chat"}
                  onClick={() => setTab("chat")}
                  testid="tab-chat"
                />
                <TabPill
                  label="Review"
                  icon={ClipboardCheck}
                  active={tab === "review"}
                  onClick={() => setTab("review")}
                  disabled={reviewItems.length === 0}
                  testid="tab-review"
                />
                <TabPill
                  label="Score"
                  icon={Gauge}
                  active={tab === "score"}
                  onClick={() => setTab("score")}
                  disabled={!assessment}
                  testid="tab-score"
                />
                <TabPill
                  label="7-Day Diet"
                  icon={Utensils}
                  active={tab === "diet"}
                  onClick={() => setTab("diet")}
                  disabled={!assessment}
                  testid="tab-diet"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  data-testid="chatbot-restart-btn"
                  onClick={restart}
                  disabled={starting || sending}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs btn-outline-glass text-zinc-300 disabled:opacity-50"
                >
                  <RefreshCcw className="w-3 h-3" />
                  Restart
                </button>
                <button
                  data-testid="chatbot-close-btn"
                  onClick={closeChatbot}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 hover:border-cyan-400/40 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Progress */}
            <div className="px-5 sm:px-7 py-3 bg-black/20">
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#0B3D91] to-cyan-400 transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="mt-1.5 flex justify-between text-[10px] text-zinc-500 uppercase tracking-[0.15em]">
                <span>
                  {assessment
                    ? "Complete"
                    : `Question ${Math.min(progress + 1, total)} of ${total}`}
                </span>
                <span>{progressPct}%</span>
              </div>
            </div>

            {/* Mobile tab pills */}
            <div className="sm:hidden flex items-center gap-1 mx-5 mt-2 rounded-full bg-white/[0.03] border border-white/10 p-1">
              <TabPill
                label="Chat"
                icon={Bot}
                active={tab === "chat"}
                onClick={() => setTab("chat")}
                testid="m-tab-chat"
                compact
              />
              <TabPill
                label="Review"
                icon={ClipboardCheck}
                active={tab === "review"}
                onClick={() => setTab("review")}
                disabled={reviewItems.length === 0}
                testid="m-tab-review"
                compact
              />
              <TabPill
                label="Score"
                icon={Gauge}
                active={tab === "score"}
                onClick={() => setTab("score")}
                disabled={!assessment}
                testid="m-tab-score"
                compact
              />
              <TabPill
                label="Diet"
                icon={Utensils}
                active={tab === "diet"}
                onClick={() => setTab("diet")}
                disabled={!assessment}
                testid="m-tab-diet"
                compact
              />
            </div>

            {/* Content area */}
            <div className="relative flex-1 overflow-hidden" style={{ height: "calc(100% - 210px)" }}>
              {emergency ? (
                <EmergencyView
                  message={emergency}
                  onRestart={restart}
                  onContinueAnyway={() => setEmergency(null)}
                />
              ) : (
                <>
                  {tab === "chat" && (
                    <ChatView
                      scrollRef={scrollRef}
                      messages={messages}
                      sending={sending}
                      assessment={assessment}
                      input={input}
                      setInput={setInput}
                      sendMessage={sendMessage}
                      starting={starting}
                    />
                  )}
                  {tab === "review" && (
                    <ReviewView
                      items={reviewItems}
                      onEdit={editAnswer}
                      onConfirm={confirmAndGenerate}
                      confirming={confirming}
                      hasAssessment={!!assessment}
                    />
                  )}
                  {tab === "score" && (
                    <ScoreView assessment={assessment} onViewDiet={() => setTab("diet")} />
                  )}
                  {tab === "diet" && <DietView assessment={assessment} />}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

const TabPill = ({ label, icon: Icon, active, onClick, disabled, testid, compact }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-1.5 ${compact ? "px-3 py-1.5 text-[11px] flex-1 justify-center" : "px-3.5 py-1.5 text-xs"} rounded-full font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
      active
        ? "bg-gradient-to-r from-[#0B3D91] to-cyan-600 text-white shadow-[0_0_15px_rgba(0,229,255,0.3)]"
        : "text-zinc-400 hover:text-white"
    }`}
  >
    <Icon className="w-3 h-3" />
    {label}
  </button>
);

const ChatView = ({
  scrollRef,
  messages,
  sending,
  assessment,
  input,
  setInput,
  sendMessage,
  starting,
}) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const toggleVoice = async () => {
    const SR =
      typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      toast.error("Voice input isn't supported in this browser. Try Chrome.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    // Explicitly request mic permission first — otherwise on some browsers
    // the recognition just silently fails with 'not-allowed'.
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Immediately release the mic stream — SpeechRecognition manages its own.
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch (permErr) {
      toast.error(
        "Microphone permission denied. Enable it in your browser settings."
      );
      return;
    }
    const rec = new SR();
    rec.lang = "en-IN";
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = Array.from(e.results)
        .map((r) => r[0].transcript)
        .join(" ")
        .trim();
      if (transcript) {
        setInput((prev) => (prev ? prev + " " : "") + transcript);
      }
    };
    rec.onerror = (e) => {
      const benign = ["no-speech", "aborted"];
      if (!benign.includes(e.error)) {
        toast.error(
          `Voice error: ${e.error === "not-allowed" ? "microphone blocked" : e.error}`
        );
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    try {
      rec.start();
    } catch {
      setListening(false);
      toast.error("Voice input couldn't start. Please try again.");
    }
  };

  return (
    <div className="flex flex-col h-full">
    <div
      ref={scrollRef}
      data-testid="chat-scroll"
      className="flex-1 overflow-y-auto px-5 sm:px-7 py-6 space-y-4"
    >
      {messages.map((m, i) => (
        <ChatBubble key={i} role={m.role} text={m.text} />
      ))}
      {sending && <TypingBubble />}
      {assessment && (
        <div className="mt-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-[#0B3D91]/20 border border-cyan-400/30 p-4 text-sm text-zinc-200 flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
          <div>
            Your assessment is ready — check the{" "}
            <span className="text-cyan-400 font-medium">Score</span> and{" "}
            <span className="text-cyan-400 font-medium">7-Day Diet</span> tabs
            above. You can also ask me any health question below.
          </div>
        </div>
      )}
    </div>

    <form
      onSubmit={sendMessage}
      className="border-t border-white/5 bg-black/40 backdrop-blur-xl px-5 sm:px-7 py-4"
    >
      <div className="flex items-center gap-2 rounded-full bg-white/[0.04] border border-white/10 focus-within:border-cyan-400/40 px-4 py-2.5 transition-colors">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending || starting}
          placeholder={
            assessment
              ? "Ask me anything about your health, score or diet plan…"
              : starting
                ? "Starting…"
                : "Type your answer…"
          }
          className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-500 disabled:opacity-50"
        />
        <button
          type="button"
          data-testid="voice-input-btn"
          onClick={toggleVoice}
          disabled={sending || starting}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
            listening
              ? "bg-red-500 text-white shadow-[0_0_15px_rgba(229,57,53,0.5)] animate-pulse"
              : "bg-white/[0.05] border border-white/10 text-zinc-300 hover:border-cyan-400/40"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
          aria-label={listening ? "Stop voice input" : "Start voice input"}
        >
          {listening ? (
            <MicOff className="w-4 h-4" />
          ) : (
            <Mic className="w-4 h-4" />
          )}
        </button>
        <button
          data-testid="chat-send-btn"
          type="submit"
          disabled={sending || !input.trim()}
          className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_15px_rgba(0,229,255,0.4)] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 text-white animate-spin" />
          ) : (
            <Send className="w-4 h-4 text-white" />
          )}
        </button>
      </div>
    </form>
  </div>
  );
};

const FACTOR_ICONS = {
  sleep: MoonIcon,
  activity: Activity,
  hydration: Droplets,
  nutrition: Salad,
  stress: Brain,
  habits: Cigarette,
  family: Users,
  age: User,
  gender: User,
  condition: HeartPulse,
};

const IMPACT_STYLE = {
  positive: {
    Icon: TrendingUp,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-400/30",
    label: "Helping",
  },
  negative: {
    Icon: TrendingDown,
    color: "text-rose-400",
    bg: "bg-rose-500/10",
    border: "border-rose-400/30",
    label: "Hurting",
  },
  neutral: {
    Icon: Minus,
    color: "text-zinc-400",
    bg: "bg-white/[0.04]",
    border: "border-white/10",
    label: "Neutral",
  },
};

const CONFIDENCE_STYLE = {
  Low: { color: "text-amber-400", border: "border-amber-400/30", bg: "bg-amber-500/10" },
  Medium: { color: "text-cyan-300", border: "border-cyan-400/30", bg: "bg-cyan-500/10" },
  High: { color: "text-emerald-400", border: "border-emerald-400/30", bg: "bg-emerald-500/10" },
};

const QUESTION_LABELS = {
  symptoms: "Urgent symptoms",
  age: "Age",
  gender: "Gender",
  height: "Height",
  weight: "Weight",
  sleep: "Sleep",
  activity: "Exercise",
  diet: "Diet",
  smoking: "Smoking",
  alcohol: "Alcohol",
  hydration: "Water intake",
  pre_existing: "Pre-existing conditions",
  medications: "Medications",
  allergies: "Allergies",
  family_history: "Family history",
  stress: "Stress level",
};

const ReviewView = ({ items, onEdit, onConfirm, confirming, hasAssessment }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  if (!items || items.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Finish answering the questions to review your information.
      </div>
    );
  }

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft(item.answer);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft("");
  };

  const saveEdit = async (item) => {
    if (!draft.trim() || draft.trim() === item.answer) {
      cancelEdit();
      return;
    }
    const ok = await onEdit(item.id, draft.trim());
    if (ok) {
      setEditingId(null);
      setDraft("");
    }
  };

  return (
    <div
      data-testid="review-view"
      className="h-full overflow-y-auto px-5 sm:px-10 py-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400 mb-3">
            Review Your Information
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            Please double-check everything below.
          </h3>
          <p className="mt-2 text-sm text-zinc-400 max-w-xl">
            Accuracy matters — your Health Risk Score is calculated from these
            answers. Edit anything that isn&apos;t right, then confirm when you&apos;re
            ready.
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-2">
        {items.map((item) => {
          const label = QUESTION_LABELS[item.id] || item.id;
          const isEditing = editingId === item.id;
          return (
            <div
              key={item.id}
              data-testid={`review-item-${item.id}`}
              className="glass-card rounded-2xl p-4 border border-white/10"
            >
              <div className="flex items-start gap-4">
                <div className="w-28 shrink-0 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold pt-1">
                  {label}
                </div>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex flex-col gap-2">
                      <textarea
                        data-testid={`review-input-${item.id}`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={2}
                        className="w-full rounded-xl bg-black/40 border border-cyan-400/40 focus:border-cyan-400 outline-none px-3 py-2 text-sm text-white resize-none"
                      />
                      <div className="flex gap-2">
                        <button
                          data-testid={`review-save-${item.id}`}
                          onClick={() => saveEdit(item)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/30 transition-colors"
                        >
                          <Save className="w-3 h-3" />
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs btn-outline-glass text-zinc-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-100 whitespace-pre-wrap">
                      {item.answer}
                    </div>
                  )}
                </div>
                {!isEditing && !hasAssessment && (
                  <button
                    data-testid={`review-edit-${item.id}`}
                    onClick={() => startEdit(item)}
                    className="shrink-0 w-8 h-8 rounded-full bg-white/[0.04] border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-400/10 flex items-center justify-center transition-colors"
                    aria-label="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5 text-zinc-300" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!hasAssessment ? (
        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            data-testid="confirm-generate-btn"
            onClick={onConfirm}
            disabled={confirming || editingId !== null}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium btn-glow text-white animate-pulse-glow disabled:opacity-60 disabled:cursor-not-allowed disabled:animate-none"
          >
            {confirming ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing with AI…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Confirm & Generate my Health Score
              </>
            )}
          </button>
          <p className="text-xs text-zinc-500 max-w-md leading-relaxed">
            This may take up to 20 seconds while our AI reviews your inputs and
            builds a personalized plan.
          </p>
        </div>
      ) : (
        <div className="mt-8 rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-xs text-zinc-400">
          Assessment already generated. Restart the chat to submit different
          answers.
        </div>
      )}

      <MedicalDisclaimer />
    </div>
  );
};

const MedicalDisclaimer = () => (
  <div
    data-testid="medical-disclaimer-inline"
    className="mt-10 rounded-2xl border border-amber-400/20 bg-amber-500/[0.05] p-4 flex items-start gap-3"
  >
    <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
    <p className="text-xs text-amber-100/90 leading-relaxed">
      <span className="font-semibold text-amber-200">Medical Disclaimer:</span>{" "}
      SilentScan AI is an AI-powered health awareness tool. It does not diagnose
      diseases or replace qualified healthcare professionals. Always consult a
      doctor for medical decisions.
    </p>
  </div>
);

const EmergencyView = ({ message, onRestart, onContinueAnyway }) => (
  <div
    data-testid="emergency-screen"
    className="h-full overflow-y-auto px-5 sm:px-10 py-10"
  >
    <div className="max-w-2xl mx-auto text-center">
      <div className="mx-auto w-20 h-20 rounded-full bg-red-500/15 border border-red-400/40 flex items-center justify-center animate-pulse-glow">
        <AlertTriangle className="w-9 h-9 text-red-400" />
      </div>
      <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-400/30 text-[11px] uppercase tracking-[0.2em] text-red-300 font-semibold">
        🚨 Possible Medical Emergency Detected
      </div>
      <h3 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
        Please seek medical help now.
      </h3>
      <p className="mt-4 text-zinc-300 text-base leading-relaxed">{message}</p>
      <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
        Based on your responses, your symptoms may require immediate medical
        attention. SilentScan AI cannot diagnose emergencies. Please call{" "}
        <span className="text-red-300 font-medium">108</span> immediately or
        visit the nearest hospital.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row items-stretch justify-center gap-3">
        <a
          data-testid="emergency-call-108"
          href="tel:108"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-[#E53935] hover:bg-[#c62828] shadow-[0_10px_28px_rgba(229,57,53,0.45)] transition-colors"
        >
          <Phone className="w-4 h-4" />
          Call 108
        </a>
        <a
          data-testid="find-hospital-btn"
          href="https://www.google.com/maps/search/hospital+near+me"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium btn-outline-glass text-white"
        >
          <Stethoscope className="w-4 h-4" />
          Find Nearest Hospital
        </a>
        {onContinueAnyway && (
          <button
            data-testid="emergency-continue-anyway"
            onClick={onContinueAnyway}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-white/[0.04] border border-white/10 hover:border-white/20 text-zinc-300 transition-colors"
          >
            Continue Anyway
          </button>
        )}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-3 text-left">
        <div className="glass-card rounded-2xl p-4 flex items-start gap-3">
          <Phone className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-white">
              Call emergency services
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              India: 108. In other regions dial your local emergency number.
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-4 flex items-start gap-3">
          <Stethoscope className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-white">
              Visit the nearest hospital
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Do not attempt to drive yourself if symptoms are severe.
            </div>
          </div>
        </div>
      </div>

      <button
        data-testid="emergency-restart-btn"
        onClick={onRestart}
        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-glass text-white text-sm font-medium"
      >
        <RefreshCcw className="w-3.5 h-3.5" />
        Start a new check-in when you&apos;re safe
      </button>

      <p className="mt-8 text-[11px] text-zinc-500 leading-relaxed">
        SilentScan AI cannot replace urgent medical care.
      </p>
    </div>
  </div>
);

const ScoreView = ({ assessment, onViewDiet }) => {
  const { user } = useAuth();
  const savedRef = useRef(null);
  useEffect(() => {
    // Save to the signed-in user's private server-side history.
    // Sensitive health results are NEVER written to plain localStorage.
    if (assessment && user && savedRef.current !== assessment) {
      savedRef.current = assessment;
      saveRecord({
        type: "health_score",
        title: `Health Score — ${assessment.score}/100`,
        summary: assessment.summary || "",
        score: assessment.score,
        payload: assessment,
      }).then((ok) => ok && toast.success("Saved to your private health history"));
    }
  }, [assessment, user]);
  if (!assessment) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Finish the chat to see your score.
      </div>
    );
  }
  const conf = CONFIDENCE_STYLE[assessment.confidence] || CONFIDENCE_STYLE.Medium;
  const sub = assessment.sub_scores || {};
  return (
    <div
      data-testid="assessment-card"
      className="h-full overflow-y-auto px-5 sm:px-10 py-8"
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400">
          Personalized Health Score (0–100)
        </div>
        <div
          data-testid="confidence-badge"
          className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] uppercase tracking-widest font-semibold ${conf.bg} ${conf.border} ${conf.color}`}
        >
          <Info className="w-3 h-3" />
          {assessment.confidence} Confidence
        </div>
        <button
          data-testid="download-health-report-btn"
          onClick={() => {
            downloadHealthReportPDF(assessment);
            unlockAchievement("download_report", 25);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-cyan-500/10 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/20 transition-colors"
        >
          <Download className="w-3 h-3" />
          Download PDF
        </button>
        <ShareTwinButton assessment={assessment} />
      </div>

      {/* Viral retention hook — save this result to a free account */}
      <SaveResultCTA />

      <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
        <BigScoreRing score={assessment.score} />
        <div className="flex-1">
          <div className="text-4xl sm:text-5xl font-display font-semibold">
            {assessment.score}
            <span className="text-xl text-zinc-500">/100</span>
          </div>
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full glass-card text-xs text-cyan-300">
            <Sparkles className="w-3 h-3" />
            {assessment.label}
          </div>
          {assessment.summary && (
            <p className="mt-4 text-zinc-300 text-base leading-relaxed max-w-xl">
              {assessment.summary}
            </p>
          )}
          {assessment.confidence_reason && (
            <p className="mt-3 text-xs text-zinc-500 leading-relaxed max-w-xl">
              <span className="text-zinc-400 font-medium">
                Why this confidence level:
              </span>{" "}
              {assessment.confidence_reason} More complete information improves
              the quality of your recommendations.
            </p>
          )}
          {assessment.interpretation && (
            <div
              data-testid="ai-interpretation"
              className="mt-5 max-w-xl rounded-2xl border border-cyan-400/20 bg-cyan-500/[0.06] p-4"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
                <Sparkles className="w-3 h-3" />
                AI interpretation
              </div>
              <p className="mt-2 text-sm text-zinc-200 leading-relaxed">
                {assessment.interpretation}
              </p>
              {assessment.professional_tips?.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {assessment.professional_tips.map((t, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed"
                    >
                      <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI Health Twin — living portrait */}
      <HealthTwin
        twinScores={assessment.twin_scores}
        twinInsights={assessment.twin_insights}
        overallScore={assessment.score}
      />

      {/* Professional Dashboard: sub-scores */}
      <div className="mt-10" data-testid="sub-scores-dashboard">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-4">
          Wellness Dashboard
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <MiniScoreRing label="Lifestyle" score={sub.lifestyle ?? assessment.score} icon={HeartPulse} />
          <MiniScoreRing label="Sleep" score={sub.sleep ?? assessment.score} icon={MoonIcon} />
          <MiniScoreRing label="Activity" score={sub.activity ?? assessment.score} icon={Activity} />
          <MiniScoreRing label="Hydration" score={sub.hydration ?? assessment.score} icon={Droplets} />
          <MiniScoreRing label="Nutrition" score={sub.nutrition ?? assessment.score} icon={Salad} />
        </div>
      </div>

      {assessment.bmi != null && (
        <div className="mt-6" data-testid="bmi-card">
          <div className="glass-card rounded-2xl p-5 flex items-center gap-4 border border-white/10">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0B3D91]/30 to-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Scale className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Body Mass Index (BMI)
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <div className="font-display text-2xl font-semibold">
                  {assessment.bmi}
                </div>
                {assessment.bmi_category && (
                  <div className="text-sm text-cyan-300">
                    {assessment.bmi_category}
                  </div>
                )}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                BMI is one signal, not a diagnosis. Body composition matters too.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Explainable AI: factors */}
      {assessment.factors?.length > 0 && (
        <div className="mt-10" data-testid="factors-explainability">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
              How your Personalized Health Score (0–100) was calculated
            </div>
            <div className="text-[10px] text-cyan-400 border border-cyan-400/20 rounded-full px-2 py-0.5 uppercase tracking-widest">
              Explainable AI
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {assessment.factors.map((f, i) => {
              const Icon = FACTOR_ICONS[f.icon] || HeartPulse;
              const imp = IMPACT_STYLE[f.impact] || IMPACT_STYLE.neutral;
              const ImpactIcon = imp.Icon;
              return (
                <div
                  key={i}
                  data-testid={`factor-${i}`}
                  className={`glass-card rounded-2xl p-4 border ${imp.border}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl ${imp.bg} border ${imp.border} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${imp.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-white">
                          {f.name}
                        </div>
                        <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold ${imp.color}`}>
                          <ImpactIcon className="w-3 h-3" />
                          {imp.label}
                        </div>
                      </div>
                      {f.value && (
                        <div className="text-xs text-zinc-500 mt-0.5">
                          You said: <span className="text-zinc-300">{f.value}</span>
                        </div>
                      )}
                      {f.detail && (
                        <p className="text-xs text-zinc-300 mt-2 leading-relaxed">
                          {f.detail}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Evidence-based recommendations */}
      <div className="mt-10">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-4">
          Evidence-Based Recommendations
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {assessment.recommendations?.map((r, i) => {
            const title = typeof r === "string" ? r : r?.title;
            const why = typeof r === "object" ? r?.why : "";
            const benefit = typeof r === "object" ? r?.benefit : "";
            const consult = typeof r === "object" ? r?.consult_note : "";
            return (
              <div
                key={i}
                data-testid={`rec-${i}`}
                className="glass-card rounded-2xl p-4 border border-white/10"
              >
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cyan-400/20 flex items-center justify-center mt-0.5 shrink-0">
                    <Check className="w-3 h-3 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white leading-snug">
                      {title}
                    </div>
                    {why && (
                      <div className="mt-2">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                          Why
                        </div>
                        <p className="text-xs text-zinc-300 mt-0.5 leading-relaxed">
                          {why}
                        </p>
                      </div>
                    )}
                    {benefit && (
                      <div className="mt-2">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">
                          Expected benefit
                        </div>
                        <p className="text-xs text-zinc-300 mt-0.5 leading-relaxed">
                          {benefit}
                        </p>
                      </div>
                    )}
                    {consult && (
                      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-200/90 leading-relaxed">
                        <Stethoscope className="w-3 h-3 mt-0.5 shrink-0 text-amber-300" />
                        <span>{consult}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {assessment.problems?.length > 0 && (
        <div className="mt-10" data-testid="problems-analysis">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-4">
            Potential Areas to Watch
          </div>
          <div className="space-y-3">
            {assessment.problems.map((p, i) => (
              <div
                key={i}
                data-testid={`problem-${i}`}
                className="glass-card rounded-2xl p-5 border border-white/10"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/10 border border-amber-400/30 flex items-center justify-center">
                    <span className="text-[10px] font-semibold text-amber-300">
                      {i + 1}
                    </span>
                  </div>
                  <div className="font-display text-base font-semibold text-white">
                    {p.problem}
                  </div>
                </div>
                {p.cause && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">
                      Likely Cause
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed">
                      {p.cause}
                    </p>
                  </div>
                )}
                {p.solution && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold mb-1">
                      Suggested Approach
                    </div>
                    <p className="text-sm text-zinc-200 leading-relaxed">
                      {p.solution}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {assessment.diet_plan?.length > 0 && (
        <button
          data-testid="view-diet-btn"
          onClick={onViewDiet}
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium"
        >
          <Utensils className="w-4 h-4" />
          View your personalized 7-day diet plan
        </button>
      )}

      <MedicalDisclaimer />
    </div>
  );
};

const MiniScoreRing = ({ label, score, icon: Icon }) => {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div
      data-testid={`sub-score-${label.toLowerCase()}`}
      className="glass-card rounded-2xl p-4 flex flex-col items-center text-center"
    >
      <div className="relative w-16 h-16">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="10"
          />
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke="url(#minigrad)"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * 264} 264`}
            style={{ transition: "stroke-dasharray 1.2s ease" }}
          />
          <defs>
            <linearGradient id="minigrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00E5FF" />
              <stop offset="100%" stopColor="#0B3D91" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-base font-semibold">{pct}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-400 uppercase tracking-widest">
        {Icon && <Icon className="w-3 h-3 text-cyan-400" />}
        {label}
      </div>
    </div>
  );
};

const DietView = ({ assessment }) => {
  if (!assessment?.diet_plan?.length) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Finish the chat to unlock your diet plan.
      </div>
    );
  }
  return (
    <div
      data-testid="diet-plan"
      className="h-full overflow-y-auto px-5 sm:px-10 py-8"
    >
      <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400">
        Your 7-Day Personalized Indian Diet Plan
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
          Eat with intention.
        </h3>
        {assessment.diet_style && (
          <span
            data-testid="diet-style-badge"
            className="text-[10px] uppercase tracking-widest text-cyan-300 border border-cyan-400/30 rounded-full px-2 py-0.5"
          >
            {assessment.diet_style}
          </span>
        )}
      </div>
      <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
        Curated by SilentScan AI using foods commonly cooked in Indian homes,
        tailored to your BMI, health conditions, family history and lifestyle.
        Adjust portions to your appetite.
      </p>

      {assessment.hydration_recommendation && (
        <div
          data-testid="hydration-card"
          className="mt-6 glass-card rounded-2xl p-5 flex items-start gap-4 border border-cyan-400/20"
        >
          <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
            <Droplets className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
              Water Intake Recommendation
            </div>
            <p className="text-sm text-zinc-200 mt-1 leading-relaxed">
              {assessment.hydration_recommendation}
            </p>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {assessment.diet_plan.map((d, i) => (
          <div
            key={i}
            data-testid={`diet-day-${i}`}
            className="glass-card rounded-2xl p-5"
          >
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center text-xs font-semibold shadow-[0_0_15px_rgba(0,229,255,0.35)]">
                {i + 1}
              </div>
              <div className="font-display text-lg font-semibold">{d.day}</div>
              {d.reason && (
                <div
                  data-testid={`diet-day-${i}-reason`}
                  className="text-[11px] text-zinc-400 italic leading-snug ml-1 flex-1 min-w-[200px]"
                >
                  <span className="text-cyan-400 not-italic font-semibold mr-1">
                    Why:
                  </span>
                  {d.reason}
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <MealCell icon={Sun} label="Breakfast" text={d.breakfast} />
              <MealCell icon={Sunset} label="Lunch" text={d.lunch} />
              <MealCell icon={Moon} label="Dinner" text={d.dinner} />
              <MealCell icon={Cookie} label="Snack" text={d.snack} />
            </div>
          </div>
        ))}
      </div>

      <p className="mt-8 text-[11px] text-zinc-500 leading-relaxed">
        This meal plan is for educational purposes only. Please consult a
        registered dietitian or doctor for personalized medical nutrition
        advice, especially if you have conditions like diabetes, hypertension,
        heart or kidney disease, PCOS, or if you are pregnant.
      </p>
      <MedicalDisclaimer />
    </div>
  );
};

const MealCell = ({ icon: Icon, label, text }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-cyan-400 font-semibold mb-1.5">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    <div className="text-xs text-zinc-200 leading-relaxed">{text || "—"}</div>
  </div>
);

const ChatBubble = ({ role, text }) => {
  const isUser = role === "user";
  return (
    <div
      data-testid={`chat-bubble-${role}`}
      className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2`}
    >
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shrink-0 mt-0.5 shadow-[0_0_10px_rgba(0,229,255,0.4)]">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-gradient-to-br from-[#0B3D91] to-cyan-600 text-white rounded-br-sm"
            : "bg-white/[0.05] border border-white/5 text-zinc-100 rounded-bl-sm"
        }`}
      >
        {text}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
          <User className="w-4 h-4 text-zinc-300" />
        </div>
      )}
    </div>
  );
};

const TypingBubble = () => (
  <div className="flex gap-2 justify-start">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shrink-0 mt-0.5">
      <Bot className="w-4 h-4 text-white" />
    </div>
    <div className="bg-white/[0.05] border border-white/5 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
      <span
        className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce"
        style={{ animationDelay: "0ms" }}
      />
      <span
        className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce"
        style={{ animationDelay: "150ms" }}
      />
      <span
        className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce"
        style={{ animationDelay: "300ms" }}
      />
    </div>
  </div>
);

const BigScoreRing = ({ score }) => (
  <div className="relative w-32 h-32 sm:w-40 sm:h-40 shrink-0">
    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="8"
      />
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke="url(#biggrad)"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${(score / 100) * 264} 264`}
        style={{ transition: "stroke-dasharray 1.2s ease" }}
      />
      <defs>
        <linearGradient id="biggrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00E5FF" />
          <stop offset="100%" stopColor="#0B3D91" />
        </linearGradient>
      </defs>
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <div className="font-display text-3xl sm:text-4xl font-semibold">{score}</div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
        Wellness
      </div>
    </div>
  </div>
);

const ShareTwinButton = ({ assessment }) => {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    if (!assessment?.twin_scores) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/twin/share`,
        {
          twin_scores: assessment.twin_scores,
          twin_insights: assessment.twin_insights || [],
          overall_score: assessment.score || 0,
        }
      );
      const url = `${window.location.origin}${data.share_url}`;
      try {
        if (navigator.share) {
          await navigator.share({
            title: "My SilentScan AI Health Twin",
            text: "Meet my AI Health Twin — 6 dimensions of wellbeing.",
            url,
          });
        } else {
          await navigator.clipboard.writeText(url);
          toast.success("Share link copied");
        }
      } catch {
        // user cancelled share
      }
    } catch (e) {
      toast.error("Could not create share link");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid="share-twin-btn"
      onClick={share}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-emerald-500/10 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/20 transition-colors disabled:opacity-60"
    >
      {busy ? (
        <Spinner className="w-3 h-3 animate-spin" />
      ) : (
        <Share2 className="w-3 h-3" />
      )}
      Share Twin
    </button>
  );
};

// Viral retention hook — prompt guests to save their result to a free account.
const SaveResultCTA = () => {
  const { user, openAuth } = useAuth();
  if (user) {
    return (
      <div
        data-testid="result-saved-chip"
        className="mb-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.07] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div className="flex items-center gap-2 flex-1 text-sm text-emerald-200">
          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
          Saved to your private health history — it survives refresh and new devices.
        </div>
        <Link
          to="/history"
          data-testid="view-history-link"
          className="text-xs text-cyan-300 hover:text-cyan-200 transition-colors shrink-0"
        >
          View my history →
        </Link>
      </div>
    );
  }
  return (
    <div
      data-testid="save-result-hook"
      className="mb-6 rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-cyan-500/10 to-[#0B3D91]/25 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex-1">
        <div className="font-display text-lg font-semibold text-white">
          Don&apos;t lose this result
        </div>
        <p className="mt-1 text-sm text-zinc-300 leading-relaxed">
          Save your Health Twin and score to a free account — track your progress
          over time, on any device. Private to you, always.
        </p>
      </div>
      <button
        data-testid="save-result-cta-btn"
        onClick={openAuth}
        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium shrink-0 animate-pulse-glow"
      >
        <Sparkles className="w-4 h-4" />
        Save to a free account
      </button>
    </div>
  );
};

